import { useEffect, useState } from "react";
import { Plane } from "lucide-react";
import { fmtDateIn } from "@/lib/format";

export function useNow(ms = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export const fmtDur = (min) => {
  const m = Math.max(0, Math.round(min));
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
};

const fmtUntil = (min) => {
  const d = Math.round(min / 1440);
  return min >= 1440 ? `${d} day${d === 1 ? "" : "s"}` : fmtDur(min);
};

const fmtWhen = (iso, tz) => {
  if (!iso) return { day: "", time: "" };
  const d = new Date(iso);
  return {
    day: `${d.toLocaleDateString("en-US", { timeZone: tz, weekday: "long" })}, ${fmtDateIn(iso, tz)}`,
    time: d.toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", timeZoneName: "short" }),
  };
};

export function legLive(lg, now) {
  const p = lg.progress;
  if (!p) return null;
  const dep = p.dep_utc && Date.parse(p.dep_utc);
  const eta = p.eta_utc && Date.parse(p.eta_utc);
  const total = dep && eta ? (eta - dep) / 60000 : null;
  if (lg.status === "landed") return { p, state: "landed", f: 1, total, elapsed: total, remain: 0 };
  if (lg.status === "cancelled") return { p, state: "cancelled", f: 0, total };
  if (!["departed", "diverted"].includes(lg.status)) return { p, state: "scheduled", f: 0, total, until: dep ? (dep - now) / 60000 : null };
  if (!total) return { p, state: "en_route_no_eta", f: 0, elapsed: dep ? (now - dep) / 60000 : null };
  const f = Math.min(1, Math.max(0, (now - dep) / (eta - dep)));
  return { p, state: f >= 1 ? "arriving" : "en_route", f, total, elapsed: (now - dep) / 60000, remain: (eta - now) / 60000 };
}

const HEAD = {
  landed: ["Landed", "text-emerald-600"],
  cancelled: ["Cancelled", "text-zinc-500"],
  scheduled: ["Scheduled", "text-[#1E3A8A]"],
  en_route: ["En route", "text-emerald-600"],
  en_route_no_eta: ["En route", "text-emerald-600"],
  arriving: ["Arriving", "text-emerald-600"],
};

const subline = (L) => ({
  landed: "Arrived at the destination",
  cancelled: "This leg was cancelled",
  scheduled: L.until > 0 ? `Departs in ${fmtUntil(L.until)}` : "Awaiting departure",
  en_route: `Landing in ${fmtDur(L.remain)}`,
  en_route_no_eta: "Live progress starts once an ETA is entered",
  arriving: "Due to land now",
}[L.state]);

function End({ pt, when, align, estimated, testId }) {
  const w = fmtWhen(when, pt.tz);
  return (
    <div className={align === "right" ? "text-right" : ""} data-testid={testId}>
      <p className="font-serif-display text-2xl text-[#1E3A8A]">{pt.code}</p>
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-zinc-800">{pt.city}</p>
      <p className="text-[11px] uppercase tracking-[0.06em] text-zinc-500">{w.day || "Date TBA"}</p>
      <p className={`text-sm font-semibold text-zinc-800 ${estimated ? "italic" : ""}`}>{w.time || "Time TBA"}</p>
    </div>
  );
}

const Chip = ({ children, testId }) => <span className="px-2 py-0.5 bg-[#1E3A8A]/80 text-white text-[11px] whitespace-nowrap" data-testid={testId}>{children}</span>;

export function FlightProgressStrip({ lg, i }) {
  const now = useNow(30000);
  const L = legLive(lg, now);
  if (!L) return null;
  const [head, color] = HEAD[L.state];
  const flown = Math.round(L.p.miles * L.f);
  const pct = `${(L.f * 100).toFixed(1)}%`;
  return (
    <section className="lux-card p-5 sm:p-7" data-testid={`flight-progress-${i}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
        <p className={`text-lg font-semibold uppercase tracking-[0.06em] ${color}`} data-testid={`flight-progress-${i}-state`}>{head}</p>
        <p className={`text-sm ${color}`} data-testid={`flight-progress-${i}-subline`}>{subline(L)}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <End pt={L.p.from} when={L.p.dep_utc} testId={`flight-progress-${i}-from`} />
        <End pt={L.p.to} when={L.p.landed_utc || L.p.eta_utc} align="right" estimated={!L.p.landed_utc} testId={`flight-progress-${i}-to`} />
      </div>
      <div className="relative h-8 mt-4" data-testid={`flight-progress-${i}-bar`}>
        <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-1 bg-emerald-600/15" />
        <div className="absolute left-2 top-1/2 -translate-y-1/2 h-1 bg-emerald-700 transition-[width] duration-1000" style={{ width: `calc((100% - 1rem) * ${L.f})` }} />
        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-emerald-700" />
        <span className={`absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full ${L.f >= 1 ? "bg-emerald-700" : "bg-emerald-600/25"}`} />
        <Plane size={26} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rotate-45 text-emerald-800 fill-emerald-800 transition-[left] duration-1000"
          style={{ left: `calc(0.5rem + (100% - 1rem) * ${L.f})` }} data-testid={`flight-progress-${i}-plane`} data-progress={pct} />
      </div>
      <div className="grid grid-cols-3 items-start gap-2 mt-2 text-xs text-zinc-600">
        <div className="space-y-1"><Chip testId={`flight-progress-${i}-elapsed`}>{L.elapsed != null && L.state !== "scheduled" ? `${fmtDur(L.elapsed)} elapsed` : "Not departed"}</Chip><p>{flown} mi flown</p></div>
        <p className="text-center pt-0.5" data-testid={`flight-progress-${i}-total`}>{L.total ? `${fmtDur(L.total)} total travel time` : "Total time once an ETA is set"}</p>
        <div className="space-y-1 text-right"><Chip testId={`flight-progress-${i}-remaining`}>{L.state === "en_route" ? `${fmtDur(L.remain)} remaining` : L.state === "landed" ? "Landed" : "—"}</Chip><p>{L.p.miles - flown} mi to go</p></div>
      </div>
    </section>
  );
}

export function TrackingLive({ t, RouteMap }) {
  const now = useNow(30000);
  const idx = Math.max(0, t.legs.findIndex((lg) => ["departed", "diverted"].includes(lg.status)) >= 0
    ? t.legs.findIndex((lg) => ["departed", "diverted"].includes(lg.status))
    : t.legs.findIndex((lg) => ["scheduled", "delayed"].includes(lg.status)) >= 0
      ? t.legs.findIndex((lg) => ["scheduled", "delayed"].includes(lg.status))
      : t.legs.length - 1);
  const L = legLive(t.legs[idx], now);
  const legs = t.legs.map((l) => ({ ...l, to_airport: l.diverted_to || l.to_airport }));
  return (
    <>
      <div className="mt-10"><RouteMap legs={legs} className="" testId="tracking-route-map" live={{ leg: idx, t: L ? L.f : 0 }} /></div>
      <div className="mt-6"><FlightProgressStrip lg={t.legs[idx]} i={idx} /></div>
    </>
  );
}

export function FlightProgressMini({ lg, testId }) {
  const now = useNow(30000);
  const L = legLive(lg, now);
  if (!L || !["en_route", "arriving"].includes(L.state)) return null;
  return (
    <div className="sm:col-span-3 flex items-center gap-3" data-testid={testId}>
      <div className="relative flex-1 h-1 bg-emerald-600/20">
        <div className="absolute inset-y-0 left-0 bg-emerald-600" style={{ width: `${L.f * 100}%` }} />
        <Plane size={14} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rotate-45 text-emerald-600 fill-emerald-600" style={{ left: `${L.f * 100}%` }} />
      </div>
      <span className="text-[11px] text-emerald-700 whitespace-nowrap">{L.state === "arriving" ? "Due to land now" : `Landing in ${fmtDur(L.remain)}`}</span>
    </div>
  );
}
